import { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import { Loader } from "@braintrust/braintrust-ui-components";
import { updateVisitsCounter } from "@js/apps/common/actions";
import { useUser } from "@js/apps/common/hooks/use-user";
import { UniversalSearchContextProvider } from "@js/apps/universal-search/context/universal-search-context";
import { Snackbar } from "@js/components/snackbar";
import { UNEXPECTED_NETWORK_ERROR_MESSAGE } from "@js/constants";
import { useIdParam } from "@js/hooks/use-id-param";
import { AppLayout } from "@js/layouts/app";
import { getCurrentPathEncoded } from "@js/utils";

import { useGetFreelancerPublicProfileQuery } from "../api";
import { FreelancerProfileMeta } from "../components/freelancer-profile-meta";

import { FreelancerProfileLazy } from "./lazy";

import styles from "./styles.module.scss";

export const FreelancerProfilePage = () => {
  const freelancerId = useIdParam();
  const user = useUser();
  const [searchParams] = useSearchParams();
  const shouldRedirectToLogin = !user && searchParams.has("verify_identity");

  const {
    currentData: profile,
    isError,
    isLoading,
  } = useGetFreelancerPublicProfileQuery(
    freelancerId && !shouldRedirectToLogin ? freelancerId : skipToken,
  );

  useEffect(() => {
    if (!freelancerId || shouldRedirectToLogin) {
      return;
    }

    if (isError) {
      Snackbar.error(UNEXPECTED_NETWORK_ERROR_MESSAGE);
      return;
    }

    void updateVisitsCounter(freelancerId, ENUMS.VisitsCounterType.TALENT);
  }, [freelancerId, isError, shouldRedirectToLogin]);

  if (!freelancerId) {
    return <Navigate to="/page-not-found/?reloaded=true" replace />;
  }

  if (shouldRedirectToLogin) {
    return (
      <Navigate to={`/auth/login/?next=${getCurrentPathEncoded()}`} replace />
    );
  }

  return (
    <UniversalSearchContextProvider initialTab="PEOPLE">
      <AppLayout
        coreClassName={styles.profileLayout}
        className="pt0"
        bgcolor="var(--soft-violet)"
        setBgOnBody
        flexColumn
      >
        {isLoading && <Loader centered />}
        {!isLoading && !!profile && <FreelancerProfileLazy profile={profile} />}
      </AppLayout>
      {!!profile && <FreelancerProfileMeta profile={profile} />}
    </UniversalSearchContextProvider>
  );
};
